/**
 * useCollateralMap — React hook for Collateral Map feature.
 * Manages map state, GeoJSON, save/clear operations, and PDF export.
 *
 * [COLLATERAL MAP] Added 2026-07-14
 * [v12] Client-side PDF fallback using exportHtmlToPDF (same as LoanCalculator)
 * [v13] PDF: skip server-side Puppeteer entirely — use client-side HTML template directly.
 *       Improved HTML template with segment measurements table, better styling.
 */

import React, { useState, useCallback, useRef } from "react";
import { trpc } from "../lib/trpc";
import { toast as sonnerToast } from "sonner";
import {
  parseGeoJson,
  validateGeoJsonPolygon,
  buildFeatureCollection,
  getTotalAreaSqM,
  getPolygons,
  type GeoJsonPolygon,
  type GeoJsonFeatureCollection,
} from "../lib/geoJsonUtils";
import { formatAreaLao, formatAreaBreakdown } from "../lib/areaUnitConversion";
import { exportCollateralMapToPDF } from "../lib/pdf-export";
import { readValidatedPdfBlob } from "../lib/pdfClient";
import { getMapSaveFeedback } from "../lib/mapSaveFeedback";
import type { CollateralMapPDFTemplateHandle } from "../components/CollateralMapPDFTemplate";

export type MapTileLayer = "streets" | "satellite" | "hybrid" | "maptiler" | "google";

export interface CollateralMapPDFInfo {
  collateralId: number;
  collateralType?: string;
  ownerName?: string;
  areaSqM: number;
  perimeterM: number;
  gpsLatitude?: string | number;
  gpsLongitude?: string | number;
  updatedAt?: string | null;
  /** base64 PNG of the map canvas (optional — captured by CollateralMapModal) */
  mapImageBase64?: string;
  /** Side lengths in meters (optional — from SegmentLabelManager) */
  segmentLengthsM?: number[];
}

export interface UseCollateralMapOptions {
  collateralId: number;
  loanApplicationId: number;
  /** Whether the current user can edit the map */
  canEdit: boolean;
}

export interface UseCollateralMapReturn {
  // Data — G9: now FeatureCollection (multi-polygon)
  geoBoundaryJson: GeoJsonFeatureCollection | null;
  areaSqM: number;
  areaDisplay: string;
  updatedAt: string | null;
  isLoading: boolean;
  isSaving: boolean;
  isExporting: boolean;

  // Unsaved state — G9: draft is FeatureCollection (multi-polygon)
  draftGeoJson: GeoJsonFeatureCollection | null;
  hasUnsavedChanges: boolean;

  // Tile layer
  tileLayer: MapTileLayer;
  setTileLayer: (layer: MapTileLayer) => void;

  // Actions — G9: setDraftGeoJson accepts FeatureCollection
  setDraftGeoJson: (geoJson: GeoJsonFeatureCollection | null) => void;
  /** Hydrates confirmed saved geometry without treating it as a user edit. */
  hydrateSavedGeoJson: (geoJson: GeoJsonFeatureCollection | null) => void;
  /** G9: add a single polygon to the draft FeatureCollection */
  addPolygonToDraft: (polygon: GeoJsonPolygon, areaSqM: number) => void;
  /** G9: remove polygon by index from draft FeatureCollection */
  removePolygonFromDraft: (index: number) => void;
  saveBoundary: () => Promise<boolean>;
  clearBoundary: () => Promise<boolean>;
  exportPDF: (info?: CollateralMapPDFInfo, pdfTemplateRef?: React.RefObject<CollateralMapPDFTemplateHandle | null>) => Promise<void>;
  refetch: () => void;
  // G5: Boundary version history
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  boundaryHistory: Array<Record<string, any>>;
  isLoadingHistory: boolean;
  refetchHistory: () => void;
}

// ============================================================
// Client-side PDF HTML builder (same pattern as LoanCalculator)
// ============================================================

function buildCollateralMapPDFHtml(info: CollateralMapPDFInfo): string {
  const collateralTypeMap: Record<string, string> = {
    land: "ທີ່ດິນ",
    building: "ເຮືອນ/ອາຄານ",
    vehicle: "ລົດ",
    deposit: "ເງິນຝາກ",
    other: "ອື່ນໆ",
  };

  const typeLabel = info.collateralType
    ? (collateralTypeMap[info.collateralType] || info.collateralType)
    : "ຫຼັກຊັບ";

  // Area breakdown (ໄຮ່ ງານ ຕ.ວ.)
  const areaBreakdown = formatAreaBreakdown(info.areaSqM);
  const areaLao = info.areaSqM > 0 ? formatAreaLao(info.areaSqM) : "ຍັງບໍ່ໄດ້ວັດ";
  const areaM2 = info.areaSqM > 0
    ? `${Math.round(info.areaSqM).toLocaleString("en-US")} m²`
    : "-";

  const perimLabel = info.perimeterM > 0
    ? (info.perimeterM >= 1000
      ? `${(info.perimeterM / 1000).toFixed(3)} km (${Math.round(info.perimeterM).toLocaleString("en-US")} m)`
      : `${Math.round(info.perimeterM).toLocaleString("en-US")} m`)
    : "-";

  const gpsLabel = (info.gpsLatitude && info.gpsLongitude)
    ? `${Number(info.gpsLatitude).toFixed(6)}, ${Number(info.gpsLongitude).toFixed(6)}`
    : "-";

  const dateLabel = info.updatedAt
    ? new Date(info.updatedAt).toLocaleDateString("lo-LA", { year: "numeric", month: "long", day: "numeric" })
    : new Date().toLocaleDateString("lo-LA", { year: "numeric", month: "long", day: "numeric" });

  // Map image section
  const mapSection = info.mapImageBase64
    ? `<div style="margin:16px 0;border:2px solid #cbd5e1;border-radius:8px;overflow:hidden;background:#f1f5f9;">
        <img src="${info.mapImageBase64}" style="width:100%;display:block;max-height:400px;object-fit:contain;" alt="ແຜນທີ່ຫຼັກຊັບ" />
       </div>`
    : `<div style="margin:16px 0;border:2px dashed #cbd5e1;border-radius:8px;background:#f8fafc;height:180px;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:13px;">
        ບໍ່ມີຮູບແຜນທີ່
       </div>`;

  // Segment measurements table
  const segmentsSection = (info.segmentLengthsM && info.segmentLengthsM.length > 0)
    ? `<div style="margin-bottom:16px;">
        <div style="font-size:13px;font-weight:600;color:#002975;margin-bottom:8px;padding-bottom:5px;border-bottom:1px solid #e2e8f0;">ຄວາມຍາວແຕ່ລະດ້ານ</div>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr style="background:#002975;color:#fff;">
              <th style="padding:6px 10px;border:1px solid #1e40af;text-align:center;width:60px;">ດ້ານ</th>
              <th style="padding:6px 10px;border:1px solid #1e40af;text-align:right;">ຄວາມຍາວ (m)</th>
              <th style="padding:6px 10px;border:1px solid #1e40af;text-align:right;">ຄວາມຍາວ (km)</th>
            </tr>
          </thead>
          <tbody>
            ${info.segmentLengthsM.map((len, i) => `
              <tr style="background:${i % 2 === 0 ? "#fff" : "#f8fafc"}">
                <td style="padding:5px 10px;border:1px solid #e2e8f0;text-align:center;font-weight:600;color:#374151;">ດ້ານ ${i + 1}</td>
                <td style="padding:5px 10px;border:1px solid #e2e8f0;text-align:right;font-family:monospace;">${Math.round(len).toLocaleString("en-US")}</td>
                <td style="padding:5px 10px;border:1px solid #e2e8f0;text-align:right;font-family:monospace;color:#64748b;">${(len / 1000).toFixed(3)}</td>
              </tr>`).join("")}
            <tr style="background:#dbeafe;font-weight:700;">
              <td style="padding:6px 10px;border:1px solid #93c5fd;text-align:center;color:#1e40af;">ລວມ</td>
              <td style="padding:6px 10px;border:1px solid #93c5fd;text-align:right;color:#1e40af;font-family:monospace;">${Math.round(info.perimeterM).toLocaleString("en-US")}</td>
              <td style="padding:6px 10px;border:1px solid #93c5fd;text-align:right;color:#1e40af;font-family:monospace;">${(info.perimeterM / 1000).toFixed(3)}</td>
            </tr>
          </tbody>
        </table>
       </div>`
    : "";

  // Area breakdown table rows
  const areaBreakdownRows = areaBreakdown.map(({ label, value }) => `
    <tr>
      <td style="padding:7px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;width:40%;color:#374151;">${label}</td>
      <td style="padding:7px 12px;border:1px solid #e2e8f0;color:#374151;font-family:monospace;">${value}</td>
    </tr>`).join("");

  return `
    <div style="font-family:'Noto Sans Lao',Arial,sans-serif;padding:28px;background:#fff;max-width:794px;margin:0 auto;color:#1e293b;font-size:13px;">

      <!-- Header -->
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;padding-bottom:14px;border-bottom:3px solid #002975;">
        <div>
          <div style="font-size:20px;font-weight:700;color:#002975;letter-spacing:0.3px;">ລາຍງານຂອບເຂດຫຼັກຊັບ</div>
          <div style="font-size:13px;color:#64748b;margin-top:4px;">${typeLabel} — ລະຫັດ #${info.collateralId}</div>
        </div>
        <div style="text-align:right;font-size:11px;color:#64748b;line-height:1.6;">
          <div><strong>ວັນທີ:</strong> ${dateLabel}</div>
          ${info.ownerName ? `<div><strong>ເຈົ້າຂອງ:</strong> ${info.ownerName}</div>` : ""}
          <div><strong>ລະບົບ:</strong> Credit Referral System</div>
        </div>
      </div>

      <!-- Summary cards (same pattern as LoanCalculator) -->
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:18px;">
        <div style="background:#dbeafe;padding:12px;border-radius:8px;text-align:center;">
          <p style="color:#1e40af;font-size:10px;margin:0 0 4px;">ເນື້ອທີ່ (ໄຮ່ ງານ ຕ.ວ.)</p>
          <p style="color:#1e40af;font-weight:700;font-size:12px;margin:0;word-break:break-all;">${areaLao}</p>
        </div>
        <div style="background:#dcfce7;padding:12px;border-radius:8px;text-align:center;">
          <p style="color:#059669;font-size:10px;margin:0 0 4px;">ເສັ້ນຮອບວົງ</p>
          <p style="color:#059669;font-weight:700;font-size:12px;margin:0;word-break:break-all;">${perimLabel}</p>
        </div>
        <div style="background:#fef3c7;padding:12px;border-radius:8px;text-align:center;">
          <p style="color:#d97706;font-size:10px;margin:0 0 4px;">ພິກັດ GPS</p>
          <p style="color:#d97706;font-weight:700;font-size:10px;margin:0;font-family:monospace;word-break:break-all;">${gpsLabel}</p>
        </div>
      </div>

      <!-- Map Image -->
      ${mapSection}

      <!-- Measurements detail -->
      <div style="margin-bottom:16px;">
        <div style="font-size:13px;font-weight:600;color:#002975;margin-bottom:8px;padding-bottom:5px;border-bottom:1px solid #e2e8f0;">ຂໍ້ມູນການວັດແທກ</div>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <tr>
            <td style="padding:7px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;width:40%;color:#374151;">ເນື້ອທີ່ (ໄຮ່ ງານ ຕ.ວ.)</td>
            <td style="padding:7px 12px;border:1px solid #e2e8f0;color:#002975;font-weight:700;font-size:14px;">${areaLao}</td>
          </tr>
          <tr>
            <td style="padding:7px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;color:#374151;">ເນື້ອທີ່ (m²)</td>
            <td style="padding:7px 12px;border:1px solid #e2e8f0;color:#374151;font-family:monospace;">${areaM2}</td>
          </tr>
          ${areaBreakdownRows}
          <tr>
            <td style="padding:7px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;color:#374151;">ເສັ້ນຮອບວົງ</td>
            <td style="padding:7px 12px;border:1px solid #e2e8f0;color:#374151;font-family:monospace;">${perimLabel}</td>
          </tr>
          <tr>
            <td style="padding:7px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;color:#374151;">ພິກັດ GPS</td>
            <td style="padding:7px 12px;border:1px solid #e2e8f0;color:#374151;font-family:monospace;">${gpsLabel}</td>
          </tr>
          <tr>
            <td style="padding:7px 12px;background:#f8fafc;border:1px solid #e2e8f0;font-weight:600;color:#374151;">ປະເພດຫຼັກຊັບ</td>
            <td style="padding:7px 12px;border:1px solid #e2e8f0;color:#374151;">${typeLabel}</td>
          </tr>
        </table>
      </div>

      <!-- Segment measurements -->
      ${segmentsSection}

      <!-- Footer note -->
      <div style="margin-top:20px;padding:10px 14px;background:#fef9c3;border:1px solid #fde68a;border-radius:6px;font-size:11px;color:#92400e;">
        ⚠️ ຂໍ້ມູນນີ້ຜະລິດຈາກລະບົບ Credit Referral System — ໃຊ້ເພື່ອອ້າງອີງເທົ່ານັ້ນ ບໍ່ໃຊ້ແທນເອກະສານທາງການ
      </div>
      <p style="text-align:center;margin-top:16px;color:#94a3b8;font-size:10px;">Smart Credit Referral System — ສ້າງວັນທີ ${new Date().toLocaleDateString("lo-LA")}</p>
      <div style="height:40px;"></div>
    </div>
  `;
}

// ============================================================
// Hook
// ============================================================

export function useCollateralMap({
  collateralId,
  loanApplicationId,
  canEdit,
}: UseCollateralMapOptions): UseCollateralMapReturn {
  // G9: draft is now FeatureCollection (multi-polygon)
  const [draftGeoJson, setDraftGeoJsonState] = useState<GeoJsonFeatureCollection | null>(null);
  // GAP-1 FIX: ref always holds the latest draftGeoJson so saveBoundary closure is never stale
  const draftGeoJsonRef = useRef<GeoJsonFeatureCollection | null>(null);
  // GAP-MULTI FIX: ref always holds latest geoBoundaryJson so addPolygonToDraft can use it as
  // fallback when draftGeoJson is null (user adds new polygon while saved polygons exist)
  const geoBoundaryJsonRef = useRef<GeoJsonFeatureCollection | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [tileLayer, setTileLayer] = useState<MapTileLayer>("satellite");
  const updatedAtRef = useRef<string | null>(null);
  const boundaryVersionRef = useRef<number | null>(null);
  const [isExportingLocal, setIsExportingLocal] = useState(false);

  // ============================================================
  // tRPC Queries & Mutations
  // ============================================================

  const {
    data: collateralData,
    isLoading,
    refetch,
  } = trpc.collaterals.getGeoBoundary.useQuery(
    { collateralId },
    {
      enabled: collateralId > 0,
      staleTime: 30_000,
    }
  );

  const saveMutation = trpc.collaterals.saveGeoBoundary.useMutation();
  // Sprint v57: direct fetch to /api/pdf/collateral-map (same pattern as LoanCalculator)
  // No tRPC base64 overhead — server returns binary PDF stream directly

  // G5: Boundary version history
  const {
    data: boundaryHistory,
    refetch: refetchHistory,
    isLoading: isLoadingHistory,
  } = trpc.collaterals.getBoundaryHistory.useQuery(
    { collateralId, limit: 20 },
    { enabled: collateralId > 0, staleTime: 60_000 }
  );

  // ============================================================
  // Derived state
  // ============================================================

  // G9: parseGeoJson now returns FeatureCollection (normalizes legacy single polygon)
  const savedGeoJson = parseGeoJson(collateralData?.geoBoundaryJson);
  const geoBoundaryJson = savedGeoJson;
  // GAP-MULTI FIX: keep geoBoundaryJsonRef in sync so addPolygonToDraft can read it
  // (derived value, not state — must sync imperatively, not via useEffect)
  geoBoundaryJsonRef.current = geoBoundaryJson;
  // G9: totalAreaSqM = sum of all polygons in FeatureCollection
  const areaSqM = getTotalAreaSqM(savedGeoJson);
  const areaDisplay = areaSqM > 0 ? formatAreaLao(areaSqM) : "ຍັງບໍ່ໄດ້ວັດ";
  const updatedAt = collateralData?.updatedAt ? String(collateralData.updatedAt) : null;
  const boundaryVersion = Number((collateralData as any)?.mapBoundaryVersion ?? 0);
  if (boundaryVersionRef.current === null || boundaryVersion >= boundaryVersionRef.current) {
    boundaryVersionRef.current = boundaryVersion;
  }

  // ============================================================
  // Actions
  // ============================================================

  // G9: setDraftGeoJson accepts FeatureCollection
  const setDraftGeoJson = useCallback(
    (geoJson: GeoJsonFeatureCollection | null) => {
      // GAP-1 FIX: keep ref in sync so saveBoundary always reads the latest value
      draftGeoJsonRef.current = geoJson;
      setDraftGeoJsonState((prev) => {
        const isDifferent = JSON.stringify(prev) !== JSON.stringify(geoJson);
        if (isDifferent) setHasUnsavedChanges(true);
        return geoJson;
      });
    },
    []
  );

  // Saved geometry is displayed through the same draft-backed polygon manager,
  // but initial or canonical rollback hydration is not a user edit. Keeping this
  // path separate prevents a false Save/Discard dialog before collateral switching.
  const hydrateSavedGeoJson = useCallback(
    (geoJson: GeoJsonFeatureCollection | null) => {
      draftGeoJsonRef.current = geoJson;
      setDraftGeoJsonState(geoJson);
      setHasUnsavedChanges(false);
    },
    []
  );

  // G9: add a single polygon to the draft FeatureCollection
  const addPolygonToDraft = useCallback(
    (polygon: GeoJsonPolygon, polygonAreaSqM: number) => {
      setDraftGeoJsonState((prev) => {
        // GAP-MULTI FIX: when draftGeoJson is null (user hasn't edited yet), fall back to
        // geoBoundaryJson (the saved polygons) so existing polygons are preserved.
        // Without this: adding a new polygon while saved polygons exist → prev=null →
        // existingPolygons=[] → only the new polygon is kept, old ones disappear from draft.
        const base = prev ?? geoBoundaryJsonRef.current;
        const existingPolygons = base ? getPolygons(base) : [];
        const existingAreas = base
          ? base.features.map((f) => Number(f.properties?.areaSqM) || 0)
          : [];
        const newPolygons = [...existingPolygons, polygon];
        const newAreas = [...existingAreas, polygonAreaSqM];
        const fc = buildFeatureCollection(newPolygons, newAreas);
        draftGeoJsonRef.current = fc;
        setHasUnsavedChanges(true);
        return fc;
      });
    },
    []
  );

  // G9: remove polygon by index from draft FeatureCollection
  const removePolygonFromDraft = useCallback(
    (index: number) => {
      setDraftGeoJsonState((prev) => {
        if (!prev || prev.features.length <= 1) {
          // Removing last polygon = clear draft
          draftGeoJsonRef.current = null;
          setHasUnsavedChanges(true);
          return null;
        }
        const newPolygons = prev.features.filter((_, i) => i !== index);
        const newAreas = newPolygons.map((f) => Number(f.properties?.areaSqM) || 0);
        const fc = buildFeatureCollection(newPolygons, newAreas);
        draftGeoJsonRef.current = fc;
        setHasUnsavedChanges(true);
        return fc;
      });
    },
    []
  );

  const saveBoundary = useCallback(async (): Promise<boolean> => {
    if (!canEdit) {
      sonnerToast.error("ບໍ່ມີສິດ", { description: "ທ່ານບໍ່ມີສິດແກ້ໄຂຂອບເຂດຫຼັກຊັບ" });
      return false;
    }

    // GAP-1 FIX: use ref to avoid stale closure
    const currentDraft = draftGeoJsonRef.current ?? draftGeoJson;
    if (!currentDraft) {
      sonnerToast.error("ບໍ່ມີຂໍ້ມູນ", { description: "ກະລຸນາແຕ້ມຂອບເຂດກ່ອນ" });
      return false;
    }

    // G9: validate each polygon in the FeatureCollection
    const polygons = getPolygons(currentDraft);
    if (polygons.length === 0) {
      sonnerToast.error("ບໍ່ມີ polygon", { description: "ກະລຸນາແຕ້ມຂອບເຂດຢ່າງໜ້ອຍ 1 ຜືນ" });
      return false;
    }
    for (let i = 0; i < polygons.length; i++) {
      const validation = validateGeoJsonPolygon(polygons[i]);
      if (!validation.valid) {
        sonnerToast.error(`Polygon ຜືນທີ ${i + 1} ບໍ່ຖືກຕ້ອງ`, { description: validation.error });
        return false;
      }
    }

    const expectedUpdatedAt = updatedAtRef.current || updatedAt || "";
    const expectedBoundaryVersion = boundaryVersionRef.current ?? boundaryVersion;

    try {
      const result = await saveMutation.mutateAsync({
        collateralId,
        geoJson: currentDraft,
        expectedUpdatedAt,
        expectedBoundaryVersion,
      });

      if (result.success) {
        updatedAtRef.current = result.updatedAt ? String(result.updatedAt) : expectedUpdatedAt;
        boundaryVersionRef.current = Number(result.mapBoundaryVersion ?? expectedBoundaryVersion + 1);
        setHasUnsavedChanges(false);
        // Preserve committed geometry while the asynchronous refetch catches up.
        // Otherwise labels/layers survive while the mobile manager sees no draft.
        draftGeoJsonRef.current = currentDraft;
        setDraftGeoJsonState(currentDraft);
        await refetch();
        sonnerToast.success("ບັນທຶກສຳເລັດ", { description: `ຂອບເຂດຫຼັກຊັບ (${formatAreaLao(result.areaSqM)}) ຖືກບັນທຶກແລ້ວ` });
        return true;
      }
    } catch (err: any) {
      const msg = err?.message || "ເກີດຂໍ້ຜິດພາດ";
      const code = err?.data?.code || err?.shape?.data?.code || "";
      const httpStatus = err?.data?.httpStatus || "";
      console.error("[saveBoundary] error:", { msg, code, httpStatus, err });
      if (code === "CONFLICT" || msg.includes("CONFLICT")) {
        sonnerToast.error("ບັນທຶກຂອບເຂດບໍ່ສຳເລັດ", { description: getMapSaveFeedback(err), duration: 9000 });
      } else if (code === "FORBIDDEN" || httpStatus === 403) {
        sonnerToast.error("ບໍ່ມີສິດ", { description: `ທ່ານບໍ່ມີສິດແກ້ໄຂ: ${msg}` });
      } else if (code === "UNAUTHORIZED" || httpStatus === 401) {
        sonnerToast.error("ກະລຸນາ Login ໃໝ່", { description: "Session ໝົດອາຍຸ" });
      } else {
        sonnerToast.error("ບັນທຶກຂອບເຂດບໍ່ສຳເລັດ", { description: getMapSaveFeedback(err), duration: 9000 });
      }
    }
    return false;
  }, [canEdit, draftGeoJson, collateralId, updatedAt, boundaryVersion, saveMutation, refetch]);

  const clearBoundary = useCallback(async (): Promise<boolean> => {
    if (!canEdit) return false;

    const expectedUpdatedAt = updatedAtRef.current || updatedAt || "";
    const expectedBoundaryVersion = boundaryVersionRef.current ?? boundaryVersion;

    try {
      const result = await saveMutation.mutateAsync({
        collateralId,
        geoJson: null,
        expectedUpdatedAt,
        expectedBoundaryVersion,
      });

      if (result.success) {
        updatedAtRef.current = result.updatedAt ? String(result.updatedAt) : expectedUpdatedAt;
        boundaryVersionRef.current = Number(result.mapBoundaryVersion ?? expectedBoundaryVersion + 1);
        setHasUnsavedChanges(false);
        // Keep an explicit empty FeatureCollection through the refetch. `null` means
        // “no local draft is hydrated” to consumers and would incorrectly fall back
        // to the stale saved boundary until the async query returns.
        const clearedDraft = buildFeatureCollection([], []);
        draftGeoJsonRef.current = clearedDraft;
        setDraftGeoJsonState(clearedDraft);
        await refetch();
        sonnerToast.success("ລຶບຂອບເຂດສຳເລັດ", { description: "ຂໍ້ມູນຂອບເຂດຫຼັກຊັບຖືກລຶບແລ້ວ" });
        return true;
      }
    } catch (err: any) {
      sonnerToast.error("ລຶບຂອບເຂດບໍ່ສຳເລັດ", { description: getMapSaveFeedback(err), duration: 9000 });
    }
    return false;
  }, [canEdit, collateralId, updatedAt, boundaryVersion, saveMutation, refetch]);

  /**
   * Sprint v57: Export PDF via direct fetch to /api/pdf/collateral-map.
   * Same pattern as LoanCalculator — server returns binary PDF stream, no base64 overhead.
   * Uses generatePdfFromHtmlFast (browser singleton + inline Noto Sans Lao) — ~1-2s.
   */
  const exportPDF = useCallback(async (
    info?: CollateralMapPDFInfo,
    _pdfTemplateRef?: React.RefObject<CollateralMapPDFTemplateHandle | null>
  ): Promise<void> => {
    setIsExportingLocal(true);
    const toastId = sonnerToast.loading("ກຳລັງສ້າງ PDF...");
    const filename = `ຂອບເຂດຫຼັກຊັບ_${collateralId}_${new Date().toISOString().split("T")[0]}.pdf`;

    try {
      // Sprint v57: direct HTTP POST — server returns application/pdf binary
      // Sprint v61: add polygonGeoJson so server can fetch MapTiler static map image
      const payload = {
        collateralId,
        mapImageBase64: info?.mapImageBase64,
        polygonGeoJson: geoBoundaryJson ?? null,
        areaSqM: info?.areaSqM ?? areaSqM,
        perimeterM: info?.perimeterM ?? 0,
        segmentLengthsM: info?.segmentLengthsM ?? [],
        collateralType: info?.collateralType ?? "",
        ownerName: info?.ownerName ?? "",
        gpsDisplay: (info?.gpsLatitude && info?.gpsLongitude)
          ? `${Number(info.gpsLatitude).toFixed(6)}, ${Number(info.gpsLongitude).toFixed(6)}`
          : "",
        updatedAt: info?.updatedAt ?? updatedAt ?? "",
      };

      const response = await fetch("/api/pdf/collateral-map", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({})) as Record<string, unknown>;
        throw new Error((errBody.message as string) || `Server error ${response.status}`);
      }

      // Binary PDF response — do not silently download a proxy/error body as a PDF.
      const blob = await readValidatedPdfBlob(response);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);

      sonnerToast.success("Export PDF ສຳເລີດ", { id: toastId, description: `ດາວໂຫຼດ ${filename} ສຳເລີດ` });
    } catch (err: any) {
      sonnerToast.error("Export PDF ລົ້ມເຫຼວ", {
        id: toastId,
        description: err?.message || "ເກີດຂໍ້ຜິດພາດ — ກະລຸນາລອງໃໝ່",
      });
    } finally {
      setIsExportingLocal(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collateralId, areaSqM, updatedAt, geoBoundaryJson]);

  return {
    geoBoundaryJson,
    areaSqM,
    areaDisplay,
    updatedAt,
    isLoading,
    isSaving: saveMutation.isPending,
    isExporting: isExportingLocal,
    draftGeoJson,
    hasUnsavedChanges,
    tileLayer,
    setTileLayer,
    setDraftGeoJson,
    hydrateSavedGeoJson,
    addPolygonToDraft,
    removePolygonFromDraft,
    saveBoundary,
    clearBoundary,
    exportPDF,
    refetch,
    // G5: Boundary version history
    boundaryHistory: boundaryHistory ?? [],
    isLoadingHistory,
    refetchHistory,
  };
}
